<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useNotifications } from "@/composables/useNotifications";
import { useTask } from "@/composables/tasks/useTask";
import { TasksService } from "@/services/v2/tasks";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { isLikelyBathVaccinationConfiguration } from "@/utils/v2/tasks/display";

import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import ConfigurationsTable from "@/components/v2/Tasks/Vaccination/Tables/ConfigurationsTable.vue";
import BathVaccinationConfigurationsTable from "@/components/v2/Tasks/Vaccination/Tables/BathVaccinationConfigurationsTable.vue";
import HistoryList from "@/components/v2/Tasks/Vaccination/Lists/HistoryList.vue";
import WeightModal from "@/components/v2/Tasks/Vaccination/Modals/WeightModal.vue";
import WeightIcon from "@/components/v2/UI/Icons/WeightIcon.vue";
import DocumentIcon from "@/components/v2/UI/Icons/DocumentIcon.vue";
import InfoIcon from "@/components/v2/UI/Icons/InfoIcon.vue";
import RandomWeightProgressCard from "@/components/v2/Tasks/Shared/RandomWeightProgressCard.vue";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================
const router = useRouter();
const { activeTask } = useTask();
const { tanks } = useVaccination();
const { configurations } = useVaccinationConfigurations();
const { vaccineMethods } = useVaccination();
const { showTaskCompletionSuccess, showError } = useNotifications();
const {
  isIndividualWeightType,
  isNoWeightType,
  isRandomWeightType,
  getWeighedFish,
  getTargetWeightAmount
} = useWeight();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// Modal State
// ===================================================================
const selectedConfiguration = ref<any>(null);
const weightModal = ref<boolean>(false);

// ===================================================================
// Modal Handlers
// ===================================================================
const openWeightModal = (config?: any) => {
    weightModal.value = true;
    selectedConfiguration.value = config || null;
};

const goToEquipmentManagement = () => {
    router.push({ name: "VaccinationEquipmentView" });
};

// ===================================================================
// Computed Properties
// ===================================================================
// Simple loading state - consider task loaded when we have activeTask
const isLoading = computed(() => {
  return !activeTask.value;
});

// Detect if this is a bath vaccination task
const isBathTask = computed(() => {
  if (!configurations.value || configurations.value.length === 0) return false;

  return configurations.value.some(isLikelyBathVaccinationConfiguration);
});

// Detect if this is a simplified bath task (1 page workflow)
const isSimplifiedBathTask = computed(() => {
  if (!configurations.value || configurations.value.length === 0) return false;
  
  return configurations.value.some(config => {
    const bathConfig = config as any;
    return bathConfig.isSimplifiedBath === true;
  });
});

// Detect if this is a standard bath task (2 page workflow)
const isStandardBathTask = computed(() => {
  if (!configurations.value || configurations.value.length === 0) return false;
  
  return configurations.value.some(config => {
    const bathConfig = config as any;
    return bathConfig.isStandardBath === true;
  });
});

const taskTypeLabel = computed(() => {
  if (isSimplifiedBathTask.value) return 'Simplified Bath Vaccination';
  if (isStandardBathTask.value) return 'Standard Bath Vaccination';
  return 'Standard Vaccination';
});

const taskDescription = computed(() => {
  if (isSimplifiedBathTask.value) {
    return 'Review simplified bath vaccination configurations. Processes are time-based with specified durations. You can start and finish each bath process individually.';
  }
  if (isStandardBathTask.value) {
    return 'Review standard bath vaccination configurations. Processes are time-based with specified durations. You can start and finish each bath process individually.';
  }
  return 'You can make minor adjustments to tag colors, destination tanks, and destination groups if needed. All changes require justification before proceeding.';
});

// Check if configurations are empty
const hasConfigurations = computed(() => {
  return configurations.value && configurations.value.length > 0;
});
const completionValidation = computed(() => getWeightIdCompletionValidation());

// ===================================================================
// State
// ===================================================================
const instructionsModal = ref(false);
const isFinishing = ref(false);
const completionBlockModal = ref(false);

// ===================================================================
// Actions
// ===================================================================
// Open instructions modal
const toggleInstructionsModal = () => {
  instructionsModal.value = !instructionsModal.value;
};

// Next step
const handleProceed = () => {
  router.push({ name: 'VaccineVaccinations' });
};

// Handle finishing the task (for simplified bath)
const handleFinishTask = async () => {
  if (!activeTask.value) return;

  const validation = getWeightIdCompletionValidation();
  if (!validation.isValid) {
    completionBlockModal.value = true;
    return;
  }
  
  isFinishing.value = true;
  
  try {
    await TasksService.finishTask(activeTask.value);
    showTaskCompletionSuccess(activeTask.value.title);
  } catch (error) {
    console.error('Error completing vaccination task:', error);
    showError("Task Completion Failed", "Failed to complete the vaccination task. Please try again.");
  } finally {
    isFinishing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full justify-between">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center h-full">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Header Actions -->
      <div class="flex justify-between items-center mb-2 w-full">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">{{ activeTask?.title }}</h1>
        </div>
        <div v-if="isBathTask" class="flex justify-end gap-4">
          <button
              @click="goToEquipmentManagement"
              class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
            <InfoIcon />
            <span>Equipment</span>
          </button>

          <button
              v-if="!isIndividualWeightType && !isNoWeightType"
              @click="openWeightModal()"
              class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
            <WeightIcon />
            Record Weight
          </button>
          <button
              @click="router.push({name: 'VaccineWeights'})"
              class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
            <DocumentIcon />
            View Weight Records
          </button>
        </div>
      </div>

      <RandomWeightProgressCard
        v-if="isRandomWeightType"
        class="sticky top-2 z-10"
        :current="getWeighedFish"
        :target="getTargetWeightAmount"
      />

      <span class="w-full bg-secondary h-[1px] block"></span>
      
      <!-- Subtitle & Description -->
      <div class="mb-6">
        <h2 class="text-xl font-bold mb-2">{{ taskTypeLabel }}</h2>
        <p class="text-sm text-gray-600">{{ taskDescription }}</p>
      </div>

      <!-- History -->
      <HistoryList />

      <!-- Empty State Message -->
      <div v-if="!hasConfigurations && !isLoading" class="flex flex-col items-center justify-center py-12 text-center">
        <div class="w-16 h-16 mb-4 text-gray-400">
          <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Configurations Found</h3>
        <p class="text-sm text-gray-500 max-w-md">
          Vaccination configurations are being prepared. Please wait while the system generates the required configurations for this task.
        </p>
      </div>

      <!-- Configurations table -->
      <ConfigurationsTable
          v-if="!isBathTask && hasConfigurations && tanks?.length"
          :configurations="configurations"
          :vaccineMethods="vaccineMethods"
          :tanks="tanks"
      />

      <BathVaccinationConfigurationsTable
          v-if="isBathTask && hasConfigurations && tanks?.length"
          :configurations="configurations"
          :vaccineMethods="vaccineMethods"
          :tanks="tanks"
          @openWeightModal="openWeightModal"
      />
    </div>

    <!-- Footer Actions -->
    <FooterActions v-if="!isLoading">
      <template #start>
        <button
            @click="toggleInstructionsModal"
            class="w-full"
        >
          Instructions
        </button>
      </template>

      <template #end>
        <button
            v-if="!isBathTask && hasConfigurations"
            @click="handleProceed"
            class="w-full"
        >
          Proceed
        </button>

        <div v-else-if="isBathTask && hasConfigurations" class="w-full text-center">
          <button
              @click="handleFinishTask"
              :disabled="isFinishing"
              class="w-full relative"
              :class="{ 'opacity-50 cursor-not-allowed': isFinishing }"
          >
            <span v-if="isFinishing" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Finishing...
            </span>
            <span v-else>
              Finish Task
            </span>
          </button>
        </div>

        <button
            v-else-if="!hasConfigurations"
            disabled
            class="w-full opacity-50 cursor-not-allowed"
        >
          Waiting for Configurations
        </button>
      </template>
    </FooterActions>

    <!-- TODO: Enable when ready -->
    <!-- <InstructionsModal :show-modal="instructionsModal" @close="toggleInstructionsModal" /> -->
    
    <!-- Weight Modal -->
    <WeightModal
        v-if="weightModal"
        :show="weightModal"
        :configuration="selectedConfiguration"
        @close="weightModal = false"
    />
    <TaskCompletionBlockedModal
        :show="completionBlockModal"
        :reason="completionValidation.reason"
        @close="completionBlockModal = false"
    />
  </div>
</template>
